import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import Image from 'components/Common/Image'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import OfferDescription from '../OfferDescription'

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PropertyLogo = styled(Image)`
  width: ${rem(80)};
`

const OfferBadgeTagSection = styled.div`
  > * + * {
    margin-left: ${rem(8)};
  }
`

interface Props {
  offer: App.Offer
  locationCaption: string
  bundledOffers: Array<App.BundledOffer>
}

function BundleAndSavePropertyDetails(props: Props) {
  const intl = useIntl()
  const {
    offer,
    locationCaption,
    bundledOffers,
  } = props

  const offerPropertiesTitle = useMemo(
    () => offer.location || bundledOffers.map(offer => offer.property.name).join(' + '),
    [offer.location, bundledOffers])
  const getPropertyLogoAlt = (propertyName?: string) => propertyName
    ? intl.formatMessage({
        id: 'CWfDGK4FrdaFloQswiXJ',
        defaultMessage: '{propertyName} logo',
        description: 'Alt text for a property logo shown in the bundle and save property details header when the property name is known',
      }, { propertyName })
    : intl.formatMessage({
        id: 'G2tm50xP0lyVJ4CSx_pw',
        defaultMessage: 'Vendor logo',
        description: 'Fallback alt text for a property logo shown in the bundle and save property details header',
      })

  return (<>
    <Header>
      <Group direction="vertical" gap={8}>
        <BodyTextBlock variant="medium" startIcon={<LineMapMarkerIcon />} data-testid="offer-location">
          {locationCaption}
        </BodyTextBlock>
        <Heading variant="heading2" as="h1">
          <FormattedMessage
            id="DBgR9ViJ--4ucGkiFs6M"
            defaultMessage="<highlight>Bundle & Save:</highlight> {title}"
            description="Bundle and Save label with property title on the offer detail page"
            values={{
              highlight: (chunks) => <Heading variant="heading2" as="b" colour="highlight-secondary">{chunks}</Heading>,
              title: offerPropertiesTitle,
            }}
          />
        </Heading>
        <BodyText variant="large" as="h2">{offer.name}</BodyText>
      </Group>
      <CSSBreakpoint min="desktop">
        <Group direction="horizontal" verticalAlign="center" gap={16}>
          <PropertyLogo
            dpr={2}
            enhancedFormats={false}
            id={bundledOffers[0].property.logoImageId}
            alt={getPropertyLogoAlt(bundledOffers[0].property.name)}
          />
          <LinePlusIcon />
          <PropertyLogo
            dpr={2}
            enhancedFormats={false}
            id={bundledOffers[1].property.logoImageId}
            alt={getPropertyLogoAlt(bundledOffers[1].property.name)}
          />
        </Group>
      </CSSBreakpoint>
    </Header>
    {(!!offer.badge || offer.bundledWithFlightsOnly) && <OfferBadgeTagSection>
      {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
    </OfferBadgeTagSection>}
    {offer.description && <OfferDescription description={offer.description} />}
  </>)
}

export default BundleAndSavePropertyDetails
